import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  CircularProgress
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import viewExamPaperIcon from '../../../assets/images/SVG/view-exam-paper.svg';
import viewAnswerIcon from '../../../assets/images/SVG/view-answer-icon.svg';
import examService from '../../../infrastructure/services/examService';

export default function ViewOptionsModal({
  isOpen,
  onClose,
  onViewAnswers,
  examName = 'Exam Paper',
  paperId
}) {
  const [isPaperPreviewOpen, setIsPaperPreviewOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState('');

  useEffect(() => {
    let activeUrl = '';

    if (isPaperPreviewOpen && paperId) {
      const loadPdf = async () => {
        setPdfLoading(true);
        setPdfError('');
        try {
          const blob = await examService.downloadExamPaper(paperId);
          if (blob instanceof Blob) {
            const url = URL.createObjectURL(blob);
            activeUrl = url;
            setPdfUrl(url);
          } else {
            throw new Error('Failed to load paper PDF.');
          }
        } catch (err) {
          console.error(err);
          setPdfError(err.message || 'Error loading exam paper.');
        } finally {
          setPdfLoading(false);
        }
      };
      loadPdf();
    }

    return () => {
      if (activeUrl) {
        URL.revokeObjectURL(activeUrl);
      }
    };
  }, [isPaperPreviewOpen, paperId]);

  const handleClosePaperPreview = () => {
    setIsPaperPreviewOpen(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl('');
    }
    setPdfError('');
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '28px',
            p: 1.5,
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12)',
            maxWidth: '520px',
            width: '100%'
          }
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: '#94a3b8',
            bgcolor: '#f8fafc',
            '&:hover': {
              bgcolor: '#fee2e2',
              color: '#ef4444'
            },
            transition: 'all 0.2s'
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: '20px' }} />
        </IconButton>

        <DialogContent sx={{ px: 2, py: 3 }}>
          {/* Title */}
          <Typography
            align="center"
            sx={{
              fontWeight: 800,
              fontSize: '22px',
              color: '#1e293b',
              mb: 4,
              fontFamily: 'Poppins, sans-serif'
            }}
          >
            View Options
          </Typography>

          {/* Options List */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Option 1: Exam Paper */}
            <Box
              onClick={() => setIsPaperPreviewOpen(true)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2.5,
                borderRadius: '20px',
                border: '1.5px solid #e2e8f0',
                cursor: 'pointer',
                bgcolor: '#ffffff',
                transition: 'all 0.25s ease',
                minHeight: '104px',
                boxSizing: 'border-box',
                '&:hover': {
                  borderColor: '#FF6244',
                  bgcolor: '#FFFDFC',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(255, 98, 68, 0.06)'
                },
                '&:active': {
                  transform: 'translateY(0)'
                }
              }}
            >
              {/* SVG Icon */}
              <Box
                component="img"
                src={viewExamPaperIcon}
                alt="Exam Paper Icon"
                sx={{
                  width: 45,
                  height: 45,
                  flexShrink: 0
                }}
              />

              {/* Text content */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '16px',
                    color: '#1e293b',
                    fontFamily: 'Poppins, sans-serif'
                  }}
                >
                  Exam Paper
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12.5px',
                    color: '#64748b',
                    fontFamily: 'Poppins, sans-serif',
                    mt: 0.5,
                    lineHeight: 1.3
                  }}
                >
                  View the PDF of your submitted exam paper
                </Typography>
              </Box>

              {/* Arrow Chevron */}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#94a3b8"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ flexShrink: 0 }}
              >
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </Box>

            {/* Option 2: Your Answers */}
            <Box
              onClick={() => {
                onClose();
                onViewAnswers();
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2.5,
                borderRadius: '20px',
                border: '1.5px solid #e2e8f0',
                cursor: 'pointer',
                bgcolor: '#ffffff',
                transition: 'all 0.25s ease',
                minHeight: '104px',
                boxSizing: 'border-box',
                '&:hover': {
                  borderColor: '#FF6244',
                  bgcolor: '#FFFDFC',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(255, 98, 68, 0.06)'
                },
                '&:active': {
                  transform: 'translateY(0)'
                }
              }}
            >
              {/* SVG Icon */}
              <Box
                component="img"
                src={viewAnswerIcon}
                alt="Your Answers Icon"
                sx={{
                  width: 45,
                  height: 45,
                  flexShrink: 0
                }}
              />

              {/* Text content */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '16px',
                    color: '#1e293b',
                    fontFamily: 'Poppins, sans-serif'
                  }}
                >
                  Your Answers
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12.5px',
                    color: '#64748b',
                    fontFamily: 'Poppins, sans-serif',
                    mt: 0.5,
                    lineHeight: 1.3
                  }}
                >
                  View detailed answers with feedback
                </Typography>
              </Box>

              {/* Arrow Chevron */}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#94a3b8"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ flexShrink: 0 }}
              >
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Nested PDF Exam Paper Preview Dialog */}
      <Dialog
        open={isPaperPreviewOpen}
        onClose={handleClosePaperPreview}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '24px',
            p: 2.5,
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '18px',
              color: '#1e293b',
              fontFamily: 'Poppins, sans-serif'
            }}
          >
            Submitted Exam Paper: {examName}
          </Typography>
          <IconButton
            onClick={handleClosePaperPreview}
            sx={{
              bgcolor: '#fee2e2',
              color: '#ef4444',
              '&:hover': { bgcolor: '#fca5a5' },
              transition: 'all 0.2s'
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            bgcolor: '#f1f5f9',
            borderRadius: '16px',
            overflowY: 'auto',
            maxHeight: '75vh',
            border: '1.5px solid #cbd5e1',
            minHeight: '400px',
            justifyContent: pdfLoading || pdfError ? 'center' : 'flex-start',
            alignItems: pdfLoading || pdfError ? 'center' : 'stretch',
          }}
        >
          {pdfLoading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress sx={{ color: '#FF502D' }} />
              <Typography sx={{ fontFamily: 'Poppins, sans-serif', color: '#64748b', fontSize: '14px' }}>
                Downloading exam paper...
              </Typography>
            </Box>
          )}
          {pdfError && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                p: 4,
                maxWidth: 360,
                mx: 'auto'
              }}
            >
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: '24px',
                  bgcolor: '#fff5f5',
                  border: '1.5px solid #ffe2e2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2.5,
                  boxShadow: '0 8px 20px rgba(239, 68, 68, 0.05)'
                }}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="12" y1="12" x2="12" y2="16" />
                  <line x1="12" y1="18" x2="12.01" y2="18" />
                </svg>
              </Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '16px',
                  color: '#1e293b',
                  fontFamily: 'Poppins',
                  mb: 1
                }}
              >
                Preview Not Available
              </Typography>
              <Typography
                sx={{
                  fontSize: '12.5px',
                  color: '#64748b',
                  fontFamily: 'Poppins',
                  lineHeight: 1.5
                }}
              >
                {pdfError.toLowerCase().includes('not found') || pdfError.toLowerCase().includes('invalid')
                  ? "The exam paper file could not be found or has an invalid file path."
                  : pdfError}
              </Typography>
            </Box>
          )}
          {!pdfLoading && !pdfError && pdfUrl && (
            <iframe
              src={pdfUrl}
              title="Exam Paper PDF"
              width="100%"
              height="600px"
              style={{ border: 'none', borderRadius: '12px' }}
            />
          )}
        </Box>
      </Dialog>
    </>
  );
}
